import { SourcingHomeTab } from '@/shared/types/sourcing-marketplace';
import './index.less';
import cN from 'classnames';
import { useConfigProvider } from '@/lib/context';
import { forwardRef, Fragment, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { RightOutlined } from '@ant-design/icons';
import { Dropdown, MenuProps } from 'antd';
import { useRouter } from 'next/router';
import NavigationMenuHorizontalFilled from '@/components/Common/Icons/NavigationMenuHorizontalFilled';
import { useHistoryList } from '@/lib/context/HistoryListProvider';
import { isEmpty } from 'lodash-es';

interface BreadcrumbsProps {
  usedLocation: 'FDL_and_PDL' | 'FDP' | 'PDP'; // it means where this component is used.
}

const Breadcrumbs = forwardRef(({ usedLocation }: BreadcrumbsProps, ref) => {
  const { sourcingBreadcrumbs } = useHistoryList();
  const { isMobile } = useConfigProvider();

  const hasL1OrL2InBreadcrumbs = useMemo(() => {
    return sourcingBreadcrumbs.some((breadcrumb) => breadcrumb.type === 'L1' || breadcrumb.type === 'L2');
  }, [sourcingBreadcrumbs]);

  const getL1Link = (breadcrumb: string) => {
    switch (breadcrumb) {
      case SourcingHomeTab.PRODUCTS:
        return `/platform/sourcing-and-production/products`;
      case SourcingHomeTab.PACKAGING:
        return `/platform/sourcing-and-production/packaging`;
      case SourcingHomeTab.BUSINESS_AND_CREATIVE_SERVICES:
        return `/platform/sourcing-and-production/freelancers`;
      case SourcingHomeTab.WEBSITE_DEVELOPMENT:
        return `/platform/sourcing-and-production/freelancers`;
      default:
        return '/platform/sourcing-and-production/products';
    }
  };

  const hanldeMobileBreadcrumb = useCallback(() => {
    let restMenus = sourcingBreadcrumbs.slice(1, sourcingBreadcrumbs.length);
    if (usedLocation === 'FDL_and_PDL') {
      restMenus = restMenus.filter(
        (breadcrumb) => breadcrumb.type !== 'supplierName' && breadcrumb.type !== 'productName',
      );
    }

    if (restMenus && restMenus?.length === 1) {
      return (
        <>
          <RightOutlined className={'arrow-icon'} />
          <p className="sourcing-menu active">{restMenus[0]?.label}</p>
        </>
      );
    } else if (restMenus && restMenus?.length > 1) {
      const dropmenus = restMenus
        .slice(0, restMenus.length - 1)
        .map((breadcrumb) => {
          return {
            key: breadcrumb.label,
            label:
              breadcrumb?.type === 'L2' ? (
                <Link href={`/sourcing/browse/${breadcrumb?.L2Key}/suppliers`}>
                  <p className="sourcing-menu">{breadcrumb?.label}</p>
                </Link>
              ) : breadcrumb?.type === 'supplierName' && (usedLocation === 'FDP' || usedLocation === 'PDP') ? (
                <Link href={`/sourcing/suppliers/${breadcrumb?.supplierId}`}>
                  <p className="sourcing-menu">{breadcrumb?.label}</p>
                </Link>
              ) : null,
          };
        })
        .filter(({ key, label }) => isEmpty(label) === false);

      return (
        <>
          <RightOutlined className={'arrow-icon'} />
          <Dropdown overlayClassName="sourcing-breadcrumbs-dropdown" menu={{ items: dropmenus }}>
            <NavigationMenuHorizontalFilled />
          </Dropdown>

          <RightOutlined className={'arrow-icon'} />
          <p className="sourcing-menu active">{restMenus[restMenus.length - 1]?.label}</p>
        </>
      );
    }
  }, [sourcingBreadcrumbs, usedLocation]);

  const renderMobileBreadcrumbs = () => {
    return (
      <>
        {sourcingBreadcrumbs?.length > 0 && sourcingBreadcrumbs[0] && (
          <>
            <Link href={getL1Link(sourcingBreadcrumbs[0]?.L1Key)}>
              <p className="sourcing-menu">{sourcingBreadcrumbs[0]?.label}</p>
            </Link>
          </>
        )}
        {hanldeMobileBreadcrumb()}
      </>
    );
  };

  const renderDesktopBreadcrumbs = () => {
    return (
      <>
        {sourcingBreadcrumbs?.length > 0 &&
          sourcingBreadcrumbs.map((breadcrumb, index) => (
            <Fragment key={breadcrumb?.label}>
              {/* render L1 breadcrumb */}
              {breadcrumb?.type === 'L1' && index !== sourcingBreadcrumbs.length - 1 && (
                <Link href={getL1Link(breadcrumb?.L1Key)}>
                  <p className="sourcing-menu">{breadcrumb?.label}</p>
                </Link>
              )}
              {breadcrumb?.type === 'L1' && index === sourcingBreadcrumbs.length - 1 && (
                <p className="sourcing-menu active">{breadcrumb?.label}</p>
              )}

              {/* render L2 breadcrumb */}
              {breadcrumb?.type === 'L2' && index !== sourcingBreadcrumbs.length - 1 && (
                <>
                  <RightOutlined className="arrow-icon" />
                  <Link href={`/sourcing/browse/${breadcrumb?.L2Key}/suppliers`}>
                    <p className="sourcing-menu">{breadcrumb?.label}</p>
                  </Link>
                </>
              )}
              {breadcrumb?.type === 'L2' && index === sourcingBreadcrumbs.length - 1 && (
                <>
                  <RightOutlined className="arrow-icon" />
                  <p className="sourcing-menu active">{breadcrumb?.label}</p>
                </>
              )}

              {/* render supplierName breadcrumb */}
              {breadcrumb?.type === 'supplierName' &&
                index !== sourcingBreadcrumbs.length - 1 &&
                (usedLocation === 'FDP' || usedLocation === 'PDP') && (
                  <>
                    {hasL1OrL2InBreadcrumbs && <RightOutlined className="arrow-icon" />}
                    <Link href={`/sourcing/suppliers/${breadcrumb?.supplierId}`}>
                      <p className="sourcing-menu">{breadcrumb?.label}</p>
                    </Link>
                  </>
                )}
              {breadcrumb?.type === 'supplierName' &&
                index === sourcingBreadcrumbs.length - 1 &&
                (usedLocation === 'FDP' || usedLocation === 'PDP') && (
                  <>
                    {hasL1OrL2InBreadcrumbs && <RightOutlined className="arrow-icon" />}
                    <p className="sourcing-menu active">{breadcrumb?.label}</p>
                  </>
                )}

              {/* render productName breadcrumb */}
              {breadcrumb?.type === 'productName' &&
                index === sourcingBreadcrumbs.length - 1 &&
                usedLocation === 'PDP' && (
                  <>
                    <RightOutlined className="arrow-icon" />
                    <p className="sourcing-menu active">{breadcrumb?.label}</p>
                  </>
                )}
            </Fragment>
          ))}
      </>
    );
  };

  return (
    <div
      className={cN('sourcing-bread-crumbs-content-box', {
        mobile: isMobile,
      })}
    >
      {isMobile ? renderMobileBreadcrumbs() : renderDesktopBreadcrumbs()}
    </div>
  );
});

export default Breadcrumbs;
